import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../../contexts/LanguageContext';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { getCostEntries, updateCostEntry } from '../../../services/supabaseService';
import { useAuth } from '../../../contexts/AuthContext';

const ExpenseApprovalPanel = ({ onApprovalChange }) => {
  const { currentLanguage, t } = useLanguage();
  const { profile } = useAuth();
  const [pendingExpenses, setPendingExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);

  useEffect(() => {
    loadPendingExpenses();
  }, []);

  const loadPendingExpenses = async () => {
    try {
      setLoading(true);
      const allCosts = await getCostEntries();
      const pending = allCosts?.filter(cost => cost?.approvalStatus === 'pending');
      setPendingExpenses(pending || []);
    } catch (error) {
      console.error('Error loading pending expenses:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApproval = async (expenseId, status, notes = '') => {
    setProcessingId(expenseId);

    try {
      await updateCostEntry(expenseId, {
        approvalStatus: status,
        approvedBy: profile?.id,
        approvedAt: new Date()?.toISOString()
      });

      const successMessage = currentLanguage === 'fr' 
        ? `Dépense ${status === 'approved' ? 'approuvée' : 'rejetée'}` 
        : `تم ${status === 'approved' ? 'الموافقة' : 'الرفض'} على المصروف`;
      alert(successMessage);

      loadPendingExpenses();
      onApprovalChange?.();
    } catch (error) {
      console.error('Error updating approval:', error);
      alert(currentLanguage === 'fr' ? 'Erreur lors de la mise à jour' : 'خطأ في التحديث');
    } finally {
      setProcessingId(null);
    }
  };

  const handleRequestRevision = async (expenseId) => {
    const notes = prompt(currentLanguage === 'fr' ?'Entrez les notes de révision:' :'أدخل ملاحظات المراجعة:');
    
    if (notes) {
      await handleApproval(expenseId, 'revision_requested', notes);
    }
  };

  const formatCurrency = (amount) => {
    if (currentLanguage === 'ar') {
      const formattedNumber = new Intl.NumberFormat('ar-MA', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })?.format(amount);
      return `${formattedNumber} ${t('financial.currency')}`;
    } else {
      return new Intl.NumberFormat('fr-MA', {
        style: 'currency',
        currency: 'MAD',
        minimumFractionDigits: 2
      })?.format(amount);
    }
  };

  const categoryLabels = {
    accommodation: t('financial.accommodation'),
    transport: t('financial.transport'),
    meals: t('financial.meals'),
    guides: t('financial.guides'),
    visas: t('financial.visasCost'),
    other: t('financial.other')
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { color: 'bg-warning/10 text-warning', label: t('financial.pending') },
      approved: { color: 'bg-success/10 text-success', label: t('financial.approved') },
      rejected: { color: 'bg-error/10 text-error', label: t('financial.rejected') },
      revision_requested: { color: 'bg-primary/10 text-primary', label: t('financial.revisionRequested') }
    };
    return badges?.[status] || badges?.pending;
  };

  return (
    <div className="bg-card rounded-lg border border-border shadow-warm p-4 md:p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-foreground">
            {t('financial.expenseApproval')}
          </h3>
          <p className="text-sm text-muted-foreground">
            {pendingExpenses?.length} {currentLanguage === 'fr' ? 'en attente' : 'في الانتظار'}
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          iconName="RefreshCw"
          onClick={loadPendingExpenses}
          loading={loading}
        />
      </div>

      {loading ? (
        <div className="text-center py-8 text-muted-foreground">
          {currentLanguage === 'fr' ? 'Chargement...' : 'جاري التحميل...'}
        </div>
      ) : pendingExpenses?.length === 0 ? (
        <div className="text-center py-8">
          <Icon name="CheckCircle" size={48} className="text-success mx-auto mb-3" />
          <p className="text-muted-foreground">
            {currentLanguage === 'fr' ? 'Aucune dépense en attente' : 'لا توجد مصروفات في الانتظار'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {pendingExpenses?.map((expense) => (
            <div key={expense?.id} className="border border-border rounded-lg p-4 hover:shadow-warm-md transition-smooth">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-start gap-3 mb-3">
                    <div className={`w-10 h-10 rounded-lg ${getStatusBadge(expense?.approvalStatus)?.color?.split(' ')?.[0]}/20 flex items-center justify-center flex-shrink-0`}>
                      <Icon name="DollarSign" size={20} className={getStatusBadge(expense?.approvalStatus)?.color?.split(' ')?.[1]} />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-foreground">{expense?.description}</h4>
                      <div className="flex flex-wrap items-center gap-2 mt-1">
                        <span className="inline-block px-2 py-1 bg-muted text-muted-foreground text-xs rounded">
                          {categoryLabels?.[expense?.category] || expense?.category}
                        </span>
                        <span className="text-sm text-muted-foreground">
                          {expense?.groupName}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    <div>
                      <p className="text-muted-foreground caption">{t('financial.amount')}</p>
                      <p className="font-semibold text-foreground data-text">{formatCurrency(expense?.amount)}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground caption">{t('financial.quantity')}</p>
                      <p className="font-medium text-foreground data-text">{expense?.quantity}</p>
                    </div>
                    {expense?.vendor && (
                      <div>
                        <p className="text-muted-foreground caption">{t('financial.vendor')}</p>
                        <p className="font-medium text-foreground">{expense?.vendor}</p>
                      </div>
                    )}
                    <div>
                      <p className="text-muted-foreground caption">{t('financial.entryDate')}</p>
                      <p className="font-medium text-foreground data-text">{expense?.entryDate}</p>
                    </div>
                  </div>

                  {expense?.notes && (
                    <div className="mt-3 p-3 bg-muted rounded-md">
                      <p className="text-sm text-muted-foreground">{expense?.notes}</p>
                    </div>
                  )}
                </div>

                <div className="flex md:flex-col gap-2">
                  <Button
                    variant="success"
                    size="sm"
                    iconName="Check"
                    iconPosition="left"
                    onClick={() => handleApproval(expense?.id, 'approved')}
                    loading={processingId === expense?.id}
                    disabled={processingId !== null}
                  >
                    {t('financial.approve')}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Edit"
                    iconPosition="left"
                    onClick={() => handleRequestRevision(expense?.id)}
                    disabled={processingId !== null}
                  >
                    {t('financial.requestRevision')}
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    iconName="X"
                    iconPosition="left"
                    onClick={() => handleApproval(expense?.id, 'rejected')}
                    loading={processingId === expense?.id}
                    disabled={processingId !== null}
                  >
                    {t('financial.reject')}
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ExpenseApprovalPanel;